import { Component, HostListener, OnDestroy } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastController } from "@ionic/angular";
import { BaseClass } from "src/app/shared/classes/base";
import {
    BoolLimits,
    LimitConcepts,
    Limits,
    NumberLimits,
    ProcessConfig,
    QuestionConfig,
    QuestionConfigTypeEnum,
    QuestionGroup,
    QuestionTypeEnum,
} from "src/app/shared/interfaces/process-config.interface";
import { ProcessStatusEnum } from "src/app/shared/interfaces/process.interface";
import { QuestionGroupAttributesPipe } from "src/app/shared/pipes/question-group-attributes.pipe";
import { TranslationService } from "src/app/shared/services/translation.service";
import { UtilService } from "src/app/shared/services/util.service";
import { QuestionAttributePipe } from "../../shared/pipes/question-attribute.pipe";
import { EndYearEvaluation, FreeEvaluationStatus } from "../_interfaces/end-year-evaluation.interface";
import { EvaluatedUser } from "../_interfaces/evaluated-user.interface";
import { FreeEvaluationEndYearService } from "../free-evaluation-end-year.service";
import { environment } from "./../../../environments/environment";
import { distinctUntilChanged, pairwise, startWith } from "rxjs/operators";
import { ConfigService } from "src/app/shared/services/multi-clients/config.service";

@Component({
    selector: "app-manage-evaluation-end-year",
    templateUrl: "./manage-evaluation-end-year.page.html",
    styleUrls: ["./manage-evaluation-end-year.page.scss"],
})
export class ManageEvaluationEndYearPage extends BaseClass implements OnDestroy {
    public sendLimitFinalEvaluationRule = environment.features.sendLimitFinalEvaluationRule;
    public questionsType;

    private readonly _cycleId: number;
    private readonly _cycleParticipantId: number;
    private readonly _subjectUserId: string;
    private evaluationConcepts: Limits[];
    private questionGroupsIndex: number | null;
    private questionIndex: number | null;
    private lastConcept: string = "";
    private penultimateConcept: string = "";
    private finalConceptEvalField: string = "";
    private actualFinalConceptEvalField: string = "";

    public FreeEvaluationStatus = FreeEvaluationStatus;
    public processConfig: ProcessConfig;
    public editingEvaluation: EndYearEvaluation;
    public formGroup: UntypedFormGroup;
    public user: EvaluatedUser;
    public status: ProcessStatusEnum;
    public isAvailable: BoolLimits;
    public actualValues: NumberLimits;
    public limitValues: NumberLimits;
    public allQuestions: Array<QuestionConfig> = [];

    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _evaluationService: FreeEvaluationEndYearService,
        private _questionGroupsAttributePipe: QuestionGroupAttributesPipe,
        private _questionAttributePipe: QuestionAttributePipe,
        private _utilsService: UtilService,
        private _configService: ConfigService,

    ) {
        super(_toastController, _translationService);
        this._subjectUserId = this._activatedRoute.snapshot.paramMap.get("userId");
        this._cycleId = parseInt(this._activatedRoute.snapshot.paramMap.get("cycleId"), 10);
        this._cycleParticipantId = parseInt(this._activatedRoute.snapshot.paramMap.get("cycleParticipantId"), 10);
        this.questionsType = this._configService.settings.features.questionTypeAvailability.question;
    }

    ionViewWillEnter() {
        this.processConfig = this.readFromStorage("FreeEvaluationEndYear_ProcessConfig");
        if (!this.processConfig) {
            this.goBack();
            return;
        }

        this.user = this.readFromStorage("FreeEvaluationEndYear_EvaluatedUser");
        this.editingEvaluation = this.readFromStorage("FreeEvaluationEndYear_Evaluation");
        this.evaluationConcepts = this.readFromStorage("FreeEvaluationEndYear_EvaluationConceptsDone");

        if (this.processConfig.questions) {
            this.allQuestions = [...this.allQuestions, ...this.processConfig.questions];
            let groupQuestions = this._questionAttributePipe.transform(this.processConfig.questions, this.user.attributes);
            this.processConfig.questions = groupQuestions.filter((q) => !q.hideQuestion);
        } else {
            let questions: QuestionConfig[] = [];
            this.processConfig.questionsGroups.forEach((group) => {
                questions = [...questions, ...group.questions];
                group.hasCalculateQuestions = this.hasCalculateType(group);
                questions = this._questionAttributePipe.transform(questions, this.user.attributes);
                let groupQuestions = this._questionAttributePipe.transform(group.questions, this.user.attributes);
                group.questions = groupQuestions.filter((q) => !q.hideQuestion);
            });
            this.allQuestions = [...this.allQuestions, ...questions];
        }

        this.processConfig.questionsGroups = this.processConfig.questionsGroups.sort((a, b) => a.order - b.order);

        this.formGroup = this._createFormGroup(this.processConfig.questionsGroups, this.editingEvaluation);

        if (this.processConfig.questionsGroups) {
            this.processConfig.questionsGroups.forEach((group) => {
                group.questions = this._utilsService.groupQuestions(group.questions);
            });
        }

        this.initializeParameters();
        this.setIndexes();
        this.setConcept();
        this.setLimits();
        this.setAvailable();

        this.formGroup.valueChanges.pipe(startWith(this.formGroup.value), distinctUntilChanged(), pairwise()).subscribe(async ([previous, current]) => {
            if (JSON.stringify(previous) === JSON.stringify(current)) return;
            const valuesToSet = await this._utilsService.calculateRelatedQuestions(current, this.allQuestions, previous);
            valuesToSet.forEach((result) => {
                this.formGroup.get(result.field).setValue(result.value, { emitEvent: true, onlySelf: true });
            });
        });
    }

    public initializeParameters() {
        this.isAvailable = {
            last: false,
            penultimate: false,
            remaining: false,
        };
        this.actualValues = {
            last: 0,
            penultimate: 0,
            remaining: 0,
        };
        this.limitValues = {
            last: 0,
            penultimate: 0,
            remaining: 0,
        };
    }

    public setIndexes(): boolean {
        if (this.processConfig) {
            this.processConfig.questionsGroups.forEach((questionGroup, index) => {
                let qIndex = questionGroup.questions.findIndex((question) => question.type === QuestionConfigTypeEnum.FinalGrade);
                if (qIndex !== -1) {
                    this.questionIndex = qIndex;
                    this.questionGroupsIndex = index;
                }
            });

            return !(this.questionGroupsIndex === -1 || this.questionGroupsIndex === undefined || this.questionIndex === -1 || this.questionIndex === undefined);
        }
        return false;
    }

    public setConcept(): void {
        if (this.questionGroupsIndex) {
            let questionGroups = this.processConfig.questionsGroups[this.questionGroupsIndex];
            if (questionGroups && questionGroups.questions) {
                let question = questionGroups.questions[this.questionIndex];
                if (this.editingEvaluation) this.finalConceptEvalField = this.editingEvaluation[`field${question.order}`];

                if (this.formGroup) this.actualFinalConceptEvalField = this.formGroup.value[`field${question.order}`];

                let possibleConfigConcepts = question.typeConfig.possibleValues;
                this.lastConcept = possibleConfigConcepts[possibleConfigConcepts.length - 1];
                this.penultimateConcept = possibleConfigConcepts[possibleConfigConcepts.length - 2];
            }
        }
    }

    public setLimits(): void {
        this.evaluationConcepts.forEach((concept: Limits) => {
            if (concept.value === LimitConcepts.Last) {
                this.actualValues.last = concept.actual;
                this.limitValues.last = concept.max;
            }
            if (concept.value === LimitConcepts.Penultimate) {
                this.actualValues.penultimate = concept.actual;
                this.limitValues.penultimate = concept.max;
            }
            if (concept.value === LimitConcepts.Remaining) {
                this.actualValues.remaining = concept.actual;
                this.limitValues.remaining = concept.max;
            }
        });

        if (this.actualFinalConceptEvalField === this.lastConcept || this.actualFinalConceptEvalField === this.penultimateConcept) {
            this.limitValues.last += 1;
            this.limitValues.penultimate += 1;
        } else this.limitValues.remaining += 1;
    }

    public setAvailable(): void {
        this.isAvailable.last = this.actualValues.last + this.actualValues.penultimate < this.limitValues.penultimate && this.actualValues.last <= this.limitValues.last;
        this.isAvailable.penultimate = this.actualValues.last + this.actualValues.penultimate < this.limitValues.penultimate;
        this.isAvailable.remaining = this.actualValues.remaining < this.limitValues.remaining;
    }

    public goBack(): void {
        this._router.navigate(["free-evaluation-end-year/" + this._cycleId], { state: { firstView: false } });
    }

    public saveEvaluation(formGroup: UntypedFormGroup): void {
        const evaluation: EndYearEvaluation = formGroup.getRawValue();
        if (this._validateFormGroup(evaluation)) {
            evaluation.id ? this._updateEvaluation(evaluation) : this._createEvaluation(evaluation);
        } else {
            formGroup["missingFields"] = true;
            this.notify(this._translationService.getTranslation("generic.missingFields"), "danger");
            return;
        }
    }

    private _validateFormGroup(evaluation: EndYearEvaluation): boolean {
        return this.processConfig.questionsGroups.some((group) => {
            return group.questions.some((question) => {
                const controlName = "field" + question.order;
                return evaluation[controlName] && evaluation[controlName].trim() !== "";
            });
        });
    }

    private _createEvaluation(evaluation: EndYearEvaluation): void {
        this._evaluationService.saveEndYearEvaluation(evaluation).subscribe(
            () => {
                this.notify(this._translationService.getTranslation("generic.saveSuccess"), "success");
                this.goBack();
            },
            (error) => this.notify(this.getErrorNotification(error), "danger"),
        );
    }

    private _updateEvaluation(evaluation: EndYearEvaluation): void {
        this._evaluationService.updateEndYearEvaluation(evaluation).subscribe(
            () => {
                this.notify(this._translationService.getTranslation("generic.saveSuccess"), "success");
                this.goBack();
            },
            (error) => this.notify(this.getErrorNotification(error), "danger"),
        );
    }

    private _createFormGroup(groups: Array<QuestionGroup>, editingEvaluation: EndYearEvaluation = null): UntypedFormGroup {
        const formGroup = new UntypedFormGroup({
            cycleParticipantId: new UntypedFormControl(this._cycleParticipantId),
            subjectUserId: new UntypedFormControl(this._subjectUserId),
        });

        if (editingEvaluation) {
            formGroup.addControl("id", new UntypedFormControl(editingEvaluation.id));
        }
        const displayedGroups = this._questionGroupsAttributePipe.transform(groups, this.user.attributes);
        groups.forEach((group) => {
            group.questions.forEach((question) => {
                const controlName = "field" + question.order;
                const validators = displayedGroups.some((g) => g.id === group.id) && (question.required || question.goal) ? [Validators.required] : [];
                formGroup.addControl(
                    controlName,
                    new UntypedFormControl(
                        {
                            value: editingEvaluation ? editingEvaluation[controlName] : "",
                            disabled: editingEvaluation && editingEvaluation.status === FreeEvaluationStatus.Sent,
                        },
                        validators,
                    ),
                );

                question.currentIndex = question.order;
            });
        });

        return formGroup;
    }

    public isConceptAvailable() {
        if (this.setIndexes()) {
            this.setConcept();

            if (this.formGroup) {
                if (this.actualFinalConceptEvalField === this.lastConcept && this.isAvailable.last) return true;
                if (this.actualFinalConceptEvalField === this.penultimateConcept && this.isAvailable.penultimate) return true;
                if (
                    this.actualFinalConceptEvalField !== "" &&
                    this.actualFinalConceptEvalField !== this.lastConcept &&
                    this.actualFinalConceptEvalField !== this.penultimateConcept &&
                    this.isAvailable.remaining
                )
                    return true;
                if (this.actualFinalConceptEvalField === "" && (this.isAvailable.last || this.isAvailable.penultimate || this.isAvailable.remaining)) return true;
            }
        }
        return false;
    }

    private hasCalculateType(group: QuestionGroup) {
        return group.questions.some((q) => q.typeConfig.type === QuestionTypeEnum.Calculate);
    }

    @HostListener("window:beforeunload")
    ngOnDestroy() {
        localStorage.removeItem("FreeEvaluationEndYear_ProcessConfig");
        localStorage.removeItem("FreeEvaluationEndYear_Evaluation");
        localStorage.removeItem("FreeEvaluationEndYear_EvaluatedUser");
    }
}
